import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Icon } from '@independent-software/typeui/controls/Icon';
import { Tabs } from '@independent-software/typeui/controls/Tabs';
import { Header } from '@independent-software/typeui/controls/Header';
import { Flex } from '@independent-software/typeui/controls/Flex';
import { Gravatar } from '@independent-software/typeui/controls/Gravatar';
import { ToastService } from '@independent-software/typeui/controls/Toast';
import { Label } from '@independent-software/typeui/controls/Label';
import { Datum } from '@independent-software/typeui/formatters/Datum';
import { DataTable } from '@independent-software/typeui/controls/DataTable';

import { AuthContext } from '../../AuthContext';
import { Section, Viewer } from '../../modules';
import { Query } from '../../services';
import { System, SystemFactory, User, UserFactory } from '../../resource/'; 
import { ListLogs } from '../Log/ListLogs';

const ViewUser = () => {
  const auth = React.useContext(AuthContext);
  const navigate = useNavigate();
  const { id } = useParams();

  const [systems, setSystems] = React.useState([] as System[]);

  React.useEffect(() => {
    SystemFactory.getSome(auth, 0, 999, new Query("name"))
    .then((res) => setSystems(res.items));
  }, []);

  const handleEdit = () => {
    navigate(`/users/edit/${parseInt(id)}`);
  }

  const handleDelete = () => {
    ToastService.toast("User deleted.");
    navigate('/users');
  }    

  const getAccessibleSystems = (user: User) => {
    return systems.filter(system => user.systems.includes(system.id));
  }

  return (
    <Viewer<User> 
      auth={auth}
      id={parseInt(id)}
      factory={UserFactory}
      canEdit='can_edit_users'
      canDelete='can_delete_users'
      onEdit={handleEdit}
      onDelete={handleDelete}
      content={(user: User) => 
        <React.Fragment>
          <Section dark padded="very">
            <Flex.Columns count={2}>
              <Flex>
                <Flex.Row>
                  <Flex.Column>
                    <Gravatar email={user.email} size="tiny"/>
                  </Flex.Column>
                  <Flex.Column width={1}>
                    <div style={{height:'10px'}}/>
                    <Header size="h2">{user.name}</Header>
                  </Flex.Column>
                </Flex.Row>
              </Flex>
              <div style={{textAlign: 'right'}}>
                <Label basic><Icon name="id-card" color="#888"/> {(user.roles ?? []).join(', ')}</Label>
                <Label basic><Icon name="envelope" color="#888"/> {user.email}</Label>
                <Label basic>Last seen: &nbsp;{user.login_at ? <Datum.Distance value={user.login_at}/> : "never"}</Label>
              </div>
            </Flex.Columns>
          </Section>
          <Tabs underlined>
            <Tabs.Pane label="Systems">
              <DataTable data={getAccessibleSystems(user)}>
                <DataTable.Column weight={1} label="System">{(item: System) => item.name}</DataTable.Column>
              </DataTable>
            </Tabs.Pane>            
            <Tabs.Pane label="Log">
              <ListLogs auth={auth} name="user_logs" userId={user.id}/>
            </Tabs.Pane>
          </Tabs>
        </React.Fragment>}>
    </Viewer>
  );
}

export { ViewUser }
